import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Profile } from '../../lib/metrics'
import { useBreakTimer, type AlarmMode } from './useBreakTimer'
import { useSaveLog, useTodayLog } from '../../api/hooks'

/**
 * Sayaç tercihleri bu cihaza özel — sunucuya yazmıyoruz.
 *
 * Sebep: ses açık/kapalı ve alarmın ne kadar çalacağı ortama bağlı bir karar.
 * Ofisteki bilgisayarda sessiz, evdekinde susturana kadar çalsın istemek makul;
 * hesaba bağlansaydı bir cihazdaki tercih diğerini de değiştirirdi.
 */
const KEY = 'kilo.timer.v1'

function loadPrefs(): { soundOn: boolean; autoContinue: boolean; alarmMode: AlarmMode } {
  const d = { soundOn: true, autoContinue: true, alarmMode: 'until_stopped' as AlarmMode }
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return d
    const p = JSON.parse(raw) as Partial<typeof d>
    return {
      soundOn: p.soundOn ?? d.soundOn,
      autoContinue: p.autoContinue ?? d.autoContinue,
      alarmMode: p.alarmMode === 'once' ? 'once' : d.alarmMode,
    }
  } catch { return d }
}

/**
 * Sayaç state'i sekmelerin ÜSTÜNDE yaşıyor.
 *
 * Neden: daha önce TimerTab'ın içindeydi ve başka bir sekmeye geçtiğinde React
 * bileşeni söküyor, state onunla birlikte gidiyordu — sayaç sıfırlanıyordu.
 * Provider App seviyesinde durduğu için hangi sekmede olursan ol çalışmaya devam eder.
 */
interface TimerCtx extends ReturnType<typeof useBreakTimer> {
  soundOn: boolean
  setSoundOn: (v: boolean) => void
  autoContinue: boolean
  setAutoContinue: (v: boolean) => void
  alarmMode: AlarmMode
  setAlarmMode: (v: AlarmMode) => void
}

const Ctx = createContext<TimerCtx | null>(null)

export function TimerProvider({ profile, children }: { profile: Profile; children: ReactNode }) {
  const log = useTodayLog()
  const saveLog = useSaveLog()
  const [prefs, setPrefs] = useState(loadPrefs)
  const { soundOn, autoContinue, alarmMode } = prefs

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(prefs)) } catch { /* yok say */ }
  }, [prefs])

  const setSoundOn = (v: boolean) => setPrefs((p) => ({ ...p, soundOn: v }))
  const setAutoContinue = (v: boolean) => setPrefs((p) => ({ ...p, autoContinue: v }))
  const setAlarmMode = (v: AlarmMode) => setPrefs((p) => ({ ...p, alarmMode: v }))

  const timer = useBreakTimer({
    workMinutes: profile.workMinutes,
    breakMinutes: profile.breakMinutes,
    soundOn,
    alarmMode,
    autoContinue,
    initialCycles: log.data?.cycles ?? 0,
    initialStandSeconds: log.data?.standSeconds ?? 0,
    onCycleComplete: (cycles, standSeconds) =>
      saveLog.mutate({
        checks: log.data?.checks ?? [],
        cycles,
        standSeconds,
        strengthDone: log.data?.strengthDone ?? false,
        updatedAt: new Date().toISOString(),
      }),
  })

  return (
    <Ctx.Provider value={{
      ...timer, soundOn, setSoundOn, autoContinue, setAutoContinue, alarmMode, setAlarmMode,
    }}>
      {children}
    </Ctx.Provider>
  )
}

export function useTimer(): TimerCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useTimer, TimerProvider içinde kullanılmalı')
  return c
}

/**
 * Mola ekranı da App seviyesinde — mola geldiğinde hangi sekmede olursan ol
 * karşına çıksın diye. TimerTab'ın içinde kalsaydı sadece o sekme açıkken görünürdü,
 * ki uyarının bütün amacı seni ekrandan koparmak.
 */
export function BreakOverlay() {
  const t = useTimer()
  return (
    <div className="overlay" data-open={t.phase === 'break'} aria-hidden={t.phase !== 'break'}>
      <div style={{ maxWidth: 520 }}>
        <div style={{ fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--s2)', fontWeight: 600 }}>
          Mola
        </div>
        <div style={{ fontSize: 40, fontWeight: 600, letterSpacing: '-.02em', margin: '10px 0 6px' }}>
          {t.breakTask[0]}
        </div>
        <p style={{ color: 'var(--ink-2)', fontSize: 16, margin: '0 0 22px' }}>{t.breakTask[1]}</p>
        <div style={{ fontSize: 56, fontWeight: 600, letterSpacing: '-.03em' }}>
          {`${String(Math.floor(t.left / 60)).padStart(2, '0')}:${String(t.left % 60).padStart(2, '0')}`}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
          {/* Sustur, molayı bitirmez — sadece sesi keser. İkisi ayrı iş:
              alarmı duydun ama molayı kullanmak istiyorsun. */}
          {t.ringing && (
            <button onClick={t.stopAlarm} aria-label="Alarmı sustur">
              🔕 Sustur
            </button>
          )}
          <button className="primary" onClick={t.skip}>
            Molayı bitir, çalışmaya dön
          </button>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 14 }}>
          {t.ringing
            ? 'Alarm sen susturana kadar çalıyor (en fazla 5 dakika).'
            : 'Bu ekran mola bitince kendiliğinden kapanır.'}
        </p>
      </div>
    </div>
  )
}
